// ─────────────────────────────────────────────────────────────────────────────
// Archivo: MxMainList.js
// Descripción: Lista de navegación con branding Iberical e Inter Font.
// Modificado por: Asistente de Programación - 2026-04-18
// ─────────────────────────────────────────────────────────────────────────────
import React, { useImperativeHandle, createContext, useContext } from "react";
import { makeStyles } from '@material-ui/core/styles';
import '../../styles/main.css';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MenuIcon from '@material-ui/icons/Menu';
import IconButton from '@material-ui/core/IconButton';
import { Collapse } from '@material-ui/core';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { getStoreValue } from "../gx/GxStore";
import CxTooltip from '../cx/CxTooltip';
import { useTranslation } from '../hx/useTranslation';

export const MxListContext = createContext({ boSidebarCollapsed: false });

const useStyles = makeStyles({
  root: {
    paddingTop: 0,
  },
  mainList: {
    paddingTop: 0,
  },
  logo: {
    border: '0px',
    padding: props => props.boSidebarCollapsed ? '0px' : '0 12px',
    backgroundColor: 'var(--ibs_color_white)',
    display: 'flex',
    justifyContent: props => props.boSidebarCollapsed ? 'center' : 'flex-start',
    alignItems: 'center',
    height: '54px',
    overflow: 'hidden',
  },
  menuBtn: {
    padding: 5, 
    marginRight: props => props.boSidebarCollapsed ? 0 : 2,
    marginTop: props => props.boSidebarCollapsed ? -4 : 0, 
    flexShrink: 0,
  },
  logoText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    whiteSpace: 'nowrap',
    marginTop: 2, // Ajuste fino para centrar el bloque de texto con el ícono
  },
  brandTitle: {
    padding: '0px',
    color: 'var(--ibs_color_black)',
    // ─── OPTIMIZACIÓN DE FUENTE ───
    fontFamily: 'var(--ibs_brand_font_family)',
    fontSize: '14px',
    fontWeight: 800, // Extra Bold para aspecto Premium
    letterSpacing: '0.02em', 
    lineHeight: '1.1',
  },
  brandSubtitle: {
    padding: '0px',
    color: 'var(--ibs_color_gray)',
    fontFamily: 'var(--ibs_brand_font_family)',
    fontSize: '9px',
    fontWeight: 400,
    letterSpacing: '0.05em', // Subtítulo un poco más espaciado
    marginTop: 1,
  },
});

const useStylesItem = makeStyles(() => ({
  listItem: {
    height: 35,
    paddingLeft: props => props.boSidebarCollapsed ? '0px' : '10px',
    paddingRight: props => props.boSidebarCollapsed ? '0px' : '16px',
    justifyContent: props => props.boSidebarCollapsed ? 'center' : 'flex-start',
    paddingBottom: 12,
    "& .MuiTypography-body1": {
      fontFamily: 'var(--ibs_control_label_font_family)',
      fontSize: '14px',
      color: props => (props.textColor || 'var(--ibs_color_black)'),
    },
    "& .MuiListItemIcon-root": {
      minWidth: props => props.boSidebarCollapsed ? 'auto' : 35,
      display: 'flex',
      justifyContent: 'center',
      color: props => (props.iconColor || 'var(--ibs_color_black)'),
    },
  },
  listGroup: {
    height: 37,
    paddingLeft: props => props.boSidebarCollapsed ? '0px' : '10px',
    paddingRight: props => props.boSidebarCollapsed ? '0px' : '16px',
    justifyContent: props => props.boSidebarCollapsed ? 'center' : 'flex-start',
    background: 'var(--ibs_color_cy_blue)',
    color: 'var(--ibs_color_white)',
    "&:hover": {
      backgroundColor: 'var(--ibs_color_cy_blue)',
      filter: 'brightness(0.85)',
    },
    "& .MuiListItemIcon-root": {
      color: 'var(--ibs_color_white)',
      minWidth: props => props.boSidebarCollapsed ? 'auto' : 35,
      display: 'flex',
      justifyContent: 'center',
    },
    "& .MuiTypography-body1": {
      fontFamily: 'var(--ibs_control_label_font_family)',
      fontSize: '14px',
    },
  },
  innerList: {
    paddingTop: '6px',
    paddingBottom: '8px',
  }
}));

export const MxMainListItem = (props) => {
  const { boSidebarCollapsed } = useContext(MxListContext);
  const classes = useStylesItem({ ...props, boSidebarCollapsed });
  
  const iconContent = (
    <ListItemIcon>{props.icon}</ListItemIcon>
  );

  return (
    <ListItem button className={classes.listItem} onClick={props.onClick}>
      {boSidebarCollapsed ? (
        <CxTooltip text={props.text} placement="right" arrow>
          {iconContent}
        </CxTooltip>
      ) : (
        iconContent
      )}
      {!boSidebarCollapsed && <ListItemText primary={props.text} />}
    </ListItem>
  );
};

export const MxMainListGroup = (props) => {
  const { boSidebarCollapsed } = useContext(MxListContext);
  const classes = useStylesItem({ ...props, boSidebarCollapsed });
  
  const iconContent = (
    <ListItemIcon>{props.icon}</ListItemIcon>
  );

  return (
    <div>
      <ListItem button className={classes.listGroup} onClick={props.onClick}>
        {boSidebarCollapsed ? (
          <CxTooltip text={props.text} placement="right" arrow>
            {iconContent}
          </CxTooltip>
        ) : (
          iconContent
        )}
        {!boSidebarCollapsed && <ListItemText primary={props.text} />}
        {!boSidebarCollapsed && (props.group ? <ExpandLess /> : <ExpandMore />)}
      </ListItem>
      <Collapse in={props.group} timeout="auto" unmountOnExit>
        <List component="div" disablePadding className={classes.innerList}>
          {props.children}
        </List>
      </Collapse>
    </div>
  );
};

export const MxMainList = props => {
  const { boSidebarCollapsed, fxToggleSidebar, isLarge, fxToggleMobile } = props;
  const classes = useStyles({ boSidebarCollapsed });
  const [{ trans }] = useTranslation();

  useImperativeHandle(props.xref, () => ({
    getType: () => 'MxMainList'
  }));

  const getEdition = () => { 
    let strEdition = getStoreValue('StoreSession', 'txEdition').toUpperCase();
    const objMap = { 'PM': 'Premium', 'PF': 'Professional', 'JR': 'Junior', 'ST': 'Starter', 'FR': 'Free' };
    return objMap[strEdition] || strEdition;
  };

  const getApp = () => { 
    let strApp = getStoreValue('StoreSession', 'txApp').toUpperCase();
    return `${strApp.charAt(0).toUpperCase() + strApp.slice(1).toLowerCase()}`; 
  };

  const isExpiringTomorrow = () => {
    const dtExpirationApp = getStoreValue('StoreSession', 'dtExpirationApp');
    if (!dtExpirationApp) return false;
    const dtExpDate = new Date(dtExpirationApp.substring(0, 10) + 'T00:00:00');
    const dtToday = new Date();
    dtToday.setHours(0, 0, 0, 0);
    const intDiffDays = Math.floor((dtExpDate - dtToday) / (1000 * 60 * 60 * 24));
    return intDiffDays === 1;
  };

  return (
    <MxListContext.Provider value={{ boSidebarCollapsed }}>
      <div className={classes.root}>
        <div className={classes.logo}>
          <IconButton 
            className={classes.menuBtn}
            onClick={() => isLarge ? fxToggleSidebar() : (fxToggleMobile && fxToggleMobile())}
          >
            <MenuIcon style={{ color: 'var(--ibs_color_cy_blue)', fontSize: '28px' }} />
          </IconButton>
          {!boSidebarCollapsed && (
            <div 
              className={classes.logoText} 
              onClick={() => isLarge ? fxToggleSidebar() : (fxToggleMobile && fxToggleMobile())}
              style={{ cursor: 'pointer' }}
            >
              <span className={classes.brandTitle}>{`${getApp()} ${getEdition()}`.toUpperCase()}</span>
              <span className={classes.brandSubtitle}>WEB EDITION</span>
              {isExpiringTomorrow() && (
                <div style={{ color: 'red', padding: '0px 0px 0 0px', margin: '2px 0 0 0', textAlign: 'center', fontSize: '11px', fontFamily: 'var(--ibs_brand_font_family)' }}>
                  {trans('msg.subscription_expires_tomorrow').toUpperCase()}
                </div>
              )}

            </div>
          )}
        </div>
        <List className={classes.mainList}>
          {props.children}
        </List>
      </div>
    </MxListContext.Provider>
  );
};